/**
 * NotesWidget - Pins a specific journal prompt from a chosen review period
 *
 * Auto-shows the current period's response. When a new week/month/etc starts,
 * it automatically shows the new one. If the prompt text was edited and no
 * longer matches, shows a warning.
 */

import React, { useMemo, useState } from 'react';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import type { NotesWidgetConfig } from '../../types/widgets';
import type { ReviewEntry } from '../../types';
import { useWeeks } from '../../hooks/useWeeks';
import { useMonths } from '../../hooks/useMonths';
import { useDays } from '../../hooks/useDays';
import { useQuery } from '@tanstack/react-query';
import { yearsAPI } from '../../services/api';

interface NotesWidgetProps {
  config: NotesWidgetConfig;
  widgetId: string;
  windowId: string;
}

const SOURCE_LABELS: Record<NotesWidgetConfig['sourceType'], string> = {
  weekly: 'Weekly Review',
  monthly: 'Monthly Review',
  daily_start: 'Start of Day',
  daily_end: 'End of Day',
  daily_general: 'Daily Journal',
  yearly: 'Yearly Review',
};

function useReviewData(sourceType: NotesWidgetConfig['sourceType']) {
  const now = useMemo(() => new Date(), []);
  const { currentWeek, isLoading: isWeekLoading } = useWeeks(now);
  const { currentMonth, isLoading: isMonthLoading } = useMonths(now);
  const { currentDay, isLoading: isDayLoading } = useDays(now);
  const { data: currentYear, isLoading: isYearLoading } = useQuery({
    queryKey: ['years', 'current'],
    queryFn: () => yearsAPI.getCurrent(),
    enabled: sourceType === 'yearly',
  });

  switch (sourceType) {
    case 'weekly':
      return {
        reflections: currentWeek?.reflections ?? [],
        isLoading: isWeekLoading,
        periodLabel: currentWeek
          ? `Week of ${new Date(currentWeek.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          : 'This Week',
      };
    case 'monthly':
      return {
        reflections: currentMonth?.reflections ?? [],
        isLoading: isMonthLoading,
        periodLabel: currentMonth
          ? new Date(currentMonth.start_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          : 'This Month',
      };
    case 'daily_start':
      return {
        reflections: currentDay?.start_day_reflections ?? [],
        isLoading: isDayLoading,
        periodLabel: 'Today',
      };
    case 'daily_end':
      return {
        reflections: currentDay?.end_day_reflections ?? [],
        isLoading: isDayLoading,
        periodLabel: 'Today',
      };
    case 'daily_general':
      return {
        reflections: currentDay?.general_reflections ?? [],
        isLoading: isDayLoading,
        periodLabel: 'Today',
      };
    case 'yearly':
      return {
        reflections: currentYear?.reflections ?? [],
        isLoading: isYearLoading,
        periodLabel: currentYear ? String(currentYear.year) : String(new Date().getFullYear()),
      };
  }
}

export function NotesWidget({ config, widgetId, windowId }: NotesWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const { reflections, isLoading, periodLabel } = useReviewData(config.sourceType);

  const matchedEntry: ReviewEntry | undefined = reflections.find(
    (entry) => entry.prompt === config.pinnedPrompt
  );

  const sourceLabel = SOURCE_LABELS[config.sourceType];

  const notesContent = (
      <div className="p-3 h-full overflow-auto flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center h-20 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : matchedEntry ? (
          <>
            {/* Prompt as header */}
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {matchedEntry.prompt}
            </h4>

            {/* Response content */}
            {matchedEntry.response?.trim() ? (
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap flex-1">
                {matchedEntry.response}
              </p>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-4">
                <FileText className="w-6 h-6 mb-2" />
                <p className="text-xs">No response yet</p>
                <p className="text-xs mt-1">Fill this in during your {sourceLabel.toLowerCase()}</p>
              </div>
            )}

            {/* Footer with source label + period */}
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
              {sourceLabel} &middot; {periodLabel}
            </div>
          </>
        ) : (
          /* Prompt not found warning */
          <div className="flex flex-col items-center justify-center h-full text-center py-4">
            <AlertTriangle className="w-6 h-6 text-amber-500 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Prompt not found
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Not found in current {periodLabel.toLowerCase()}&apos;s {sourceLabel.toLowerCase()}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 italic px-2">
              &ldquo;{config.pinnedPrompt}&rdquo;
            </p>
            <p className="text-xs text-gray-400 mt-2">
              The prompt may have been edited. Try reconfiguring this widget.
            </p>
          </div>
        )}
      </div>
  );

  return (
    <>
      <WidgetWrapper title="Notes" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        {notesContent}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title="Notes" size="lg">
          <div className="max-h-[80vh] overflow-auto">{notesContent}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
